/**
 * Featured Product V2 (sections/featured-product-v2.liquid)
 *
 * - Gallery: the theme's Swiper for the main image, thumbnails select a slide
 *   (the classic layout adds arrows and dots for the widths without thumbnails). A video plays when its slide is
 *   chosen on screens that hover, as live's does, and pauses when another is.
 * - Tabs are native <details> sharing a `name`, so they need no script.
 * - Bundles and free gifts: when the product lists its bundle items or has
 *   gifts (`custom.gift_products_any` with any purchase, `custom.gift_products`
 *   only with a subscription), the product form's FormData is rewritten to add each
 *   line (cart.js still owns the request, loading state, errors and the cart
 *   drawer).
 * - Purchase options: the checked `selling_plan` input can set the add's quantity, line properties and a bundle's
 *   item plans (the supply plan cards, snippets/component-supply-plans.liquid), and a `[data-purchase-once]` button
 *   ("Buy once") submits a one-time purchase of one unit.
 *
 * - Landing layout: on phones a sticky add-to-cart button shows once the
 *   in-page button scrolls out of view.
 * - Product page layout (`data-gallery="peek"`): on phones the gallery shows
 *   its neighbours and loops. Its sticky bars (`data-sticky-bar`: the phone row
 *   at the foot of the screen, the desktop bar from 750px under the header)
 *   show once the page has scrolled 900px, as live's do. The desktop bar's
 *   switch flips the subscription card's switch, so the two stay one choice.
 *   The classic layout's phone bar (its chips over the button) is one of them.
 * - Classic layout, phones: chips and the guarantee's (i) open a <dialog> holding that tab's content.
 * - Bundle layout (`data-gallery-neighbours="on-touch"`), phones: the first paint shows one photo, as live's
 *   lazy-loading carousel does; the neighbours show from the first swipe or slide change.
 * - Image zoom (`enable_zoom`): live's zoom (snippets/component-product-zoom.liquid) opens on a click on the photo
 *   from 1008px, or on the + button below that, and on closing the gallery turns to the photo it closed on.
 *
 * Docs: docs/sections/featured-product-v2.md
 * @extends HTMLElement
 */
/** Live's product page bars show from this scroll position (Prestige's Sticky__Form) */
const STICKY_BAR_SCROLL = 900;

/** Where a click on the photo opens the zoom (live's Prestige: above its `pocket` breakpoint) */
const ZOOM_CLICK_QUERY = '(min-width: 1008px)';

/** Where the product page layout's gallery is sticky, and a tab marked `open_desktop` starts open */
const STICKY_GALLERY_QUERY = '(min-width: 1008px)';

/** Where a gallery video plays once its slide is chosen (live's Prestige: `supports-hover`; touch screens tap play) */
const VIDEO_AUTOPLAY_QUERY = '(hover: hover) and (pointer: fine)';

export class FeaturedProductV2 extends HTMLElement {
  swiper = null;
  bundleItems = [];

  /** Set for the length of a "Buy once" click, which adds a one-time purchase whatever plan is chosen */
  purchaseOnce = false;

  /** @type {IntersectionObserver | null} */
  stickyObserver = null;

  /** @type {{ node: Element, parent: Element, next: Node | null } | null} */
  sheetContent = null;

  /** @type {AbortController | null} */
  stickyBarAbort = null;

  /**
   * The guarantee card's outside-press and Escape listeners, ended when it closes
   * @type {AbortController | null}
   */
  cardAbort = null;

  connectedCallback() {
    this.bundleItems = this.readBundleItems();
    this.initGallery();
    this.initStickyButton();
    this.initStickyBar();
    this.openDesktopTabs();

    this.sheet?.addEventListener('close', this.onSheetClose);
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.syncFreekitImage();
    this.addEventListener('product-zoom:close', this.onZoomClose);
    this.form?.addEventListener('formdata', this.onFormData);
  }

  /**
   * A tab marked `open_desktop` starts open where the gallery is sticky: its content makes the info column long enough
   * for the gallery to follow the scroll (A+ template, PDP QA rows 46, 47, 53, 54, 58, 62). Phones keep it closed.
   */
  openDesktopTabs() {
    if (!window.matchMedia(STICKY_GALLERY_QUERY).matches) return;

    for (const tab of this.querySelectorAll('details[data-open-desktop]')) {
      tab.open = true;
    }
  }

  disconnectedCallback() {
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this.stickyObserver?.disconnect();
    this.stickyObserver = null;
    this.stickyBarAbort?.abort();
    this.stickyBarAbort = null;
    this.sheet?.removeEventListener('close', this.onSheetClose);
    this.sheet?.close();
    this.onSheetClose();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('product-zoom:close', this.onZoomClose);
    this.form?.removeEventListener('formdata', this.onFormData);
  }

  get form() {
    return this.querySelector('form[action*="/cart/add"]');
  }

  /** @returns {HTMLDialogElement | null} */
  get sheet() {
    return this.querySelector('.featured-product-v2__sheet');
  }

  /** Thumbnails, and the classic layout's dots: each selects the slide in its `data-index` */
  get thumbs() {
    return this.querySelectorAll('.featured-product-v2__thumb, .featured-product-v2__dot');
  }

  /**
   * @returns {Array<{ id: number, sellingPlan: number | null }>}
   */
  readBundleItems() {
    const data = this.querySelector('[data-bundle-items]');
    if (!data) return [];

    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      console.error('Featured product: invalid bundle data', error);
      return [];
    }
  }

  initGallery() {
    const slider = this.querySelector('.featured-product-v2__slider');
    if (!slider || typeof Swiper === 'undefined') return;

    const peek = this.dataset.gallery === 'peek';
    const slideCount = slider.querySelectorAll('.swiper-slide').length;
    // Where one slide fills the frame, a 2px gap keeps its neighbours off the frame's edge. Flush, they paint a
    // sliver there whenever the frame sits on a fractional pixel, as on 1920 screens at 125–150% display scaling.
    const peekOptions = {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: Number(this.dataset.galleryGap) || 10,
      // The classic layout starts on its first photo with nothing to the left, as live's carousel does
      loop: slideCount > 1 && this.dataset.galleryLoop !== 'false',
      loopedSlides: slideCount,
      breakpoints: {
        750: { slidesPerView: 1, centeredSlides: false, spaceBetween: 2 },
      },
    };

    this.swiper = new Swiper(slider, {
      slidesPerView: 1,
      spaceBetween: 2,
      watchOverflow: true,
      ...(peek ? peekOptions : {}),
      on: {
        slideChange: (swiper) => {
          this.markThumb(swiper.realIndex);
          this.revealNeighbours();
          this.playSlideVideo(swiper);
        },
        touchStart: () => this.revealNeighbours(),
      },
    });
  }

  /**
   * Live's gallery plays a video when its slide is chosen and pauses it when another is; touch screens leave it to the
   * video's own play button. Swiper's loop copies hold their own <video>, so every one but the shown slide's pauses.
   * @param {Swiper} swiper
   */
  playSlideVideo(swiper) {
    const shown = swiper.slides[swiper.activeIndex];

    for (const video of swiper.el.querySelectorAll('video')) {
      if (!shown?.contains(video)) video.pause();
    }

    const video = shown?.querySelector('video');
    if (!video || !window.matchMedia(VIDEO_AUTOPLAY_QUERY).matches) return;

    // A browser that refuses to play with sound before the shopper's first click leaves the poster and play button
    video.play().catch(() => {});
  }

  /**
   * Bundle layout, phones: shows the gallery's neighbours once the shopper has swiped or turned it, as live's
   * lazy-loading carousel paints them. The CSS reads the class off the element.
   */
  revealNeighbours() {
    if (this.dataset.galleryNeighbours !== 'on-touch') return;
    this.classList.add('is-gallery-touched');
  }

  /**
   * Landing layout, phones: shows the sticky button while the in-page button is scrolled past.
   */
  initStickyButton() {
    const source = this.querySelector('[data-sticky-atc-source]');
    const bar = this.querySelector('[data-sticky-atc]');
    if (!source || !bar) return;

    // The root reaches far below the screen, so the button counts as intersecting anywhere below the top edge and the
    // callback fires once it crosses that edge. A plain viewport root misses a fast scroll that jumps the button from
    // below the screen to above it, since neither position intersects.
    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { rootMargin: '0px 0px 100000px 0px' }
    );
    this.stickyObserver.observe(source);
  }

  /**
   * Product page: shows its bars once the page has scrolled 900px. The desktop bar hangs from the sticky header's
   * bottom edge, which is set on the section so the bar reads it.
   */
  initStickyBar() {
    const bars = this.querySelectorAll('[data-sticky-bar]');
    if (!bars.length) return;

    const placeBar = () => {
      const header = document.querySelector('.header')?.closest('.shopify-section');
      const top = Math.max(0, header?.getBoundingClientRect().bottom ?? 0);
      this.style.setProperty('--featured-product-v2-bar-top', `${top}px`);
    };

    let visible = null;
    const toggle = () => {
      const show = window.scrollY >= STICKY_BAR_SCROLL;
      if (show === visible) return;

      visible = show;
      if (show) placeBar();
      for (const bar of bars) bar.classList.toggle('is-visible', show);
    };

    this.stickyBarAbort = new AbortController();
    const { signal } = this.stickyBarAbort;
    window.addEventListener('scroll', toggle, { passive: true, signal });
    window.addEventListener('resize', () => visible && placeBar(), { passive: true, signal });
    toggle();
    this.initStickyPlanSwitch(signal);
  }

  /**
   * The bar's switch clicks the subscription card's own switch, and follows it back.
   * @param {AbortSignal} signal
   */
  initStickyPlanSwitch(signal) {
    const sticky = this.querySelector('[data-sticky-plan]');
    const toggle = this.querySelector('.selling-plan-cards__toggle-input');
    if (!sticky || !toggle) return;

    const sync = () => sticky.setAttribute('aria-checked', String(toggle.checked));
    sticky.addEventListener('click', () => toggle.click(), { signal });
    toggle.addEventListener('change', sync, { signal });
    sync();
  }

  /**
   * @param {number} index
   */
  markThumb(index) {
    for (const thumb of this.thumbs) {
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.index) === index));
    }
  }

  /**
   * @param {MouseEvent} event
   */
  onClick = (event) => {
    // "Buy once" submits the form as a one-time purchase. The form's handler runs inside this click (the submit and
    // its FormData are synchronous), so the flag is gone before anything else can read it.
    if (event.target.closest('[data-purchase-once]')) {
      this.purchaseOnce = true;
      window.setTimeout(() => {
        this.purchaseOnce = false;
      });
      return;
    }

    const sheetTrigger = event.target.closest('[data-sheet-source]');
    if (sheetTrigger) {
      this.openSheet(sheetTrigger);
      return;
    }

    // A click on the backdrop lands on the dialog itself; its header and body cover the rest of it
    if (event.target.closest('[data-sheet-close]') || event.target === this.sheet) {
      this.sheet.close();
      this.onSheetClose();
      return;
    }

    const step = event.target.closest('[data-slide-step]');
    if (step) {
      if (step.dataset.slideStep === 'previous') this.swiper?.slidePrev();
      else this.swiper?.slideNext();
      return;
    }

    const thumb = event.target.closest('.featured-product-v2__thumb, .featured-product-v2__dot');
    if (thumb) {
      const index = Number(thumb.dataset.index);
      if (this.swiper?.params.loop) this.swiper.slideToLoop(index);
      else this.swiper?.slideTo(index);
      return;
    }

    if (event.target.closest('[data-zoom-open]')) {
      const current = this.swiper?.slides[this.swiper.activeIndex]?.querySelector('[data-zoom-index]');
      this.openZoom(Number(current?.dataset.zoomIndex ?? 0));
      return;
    }

    // Anywhere on a photo's slide, its pills included, as live's. Swiper withholds a click that ended a swipe
    // (allowClick), so a drag across the photo does not open it
    const photo = event.target.closest('.featured-product-v2__slider .swiper-slide')?.querySelector('[data-zoom-index]');
    if (photo && window.matchMedia(ZOOM_CLICK_QUERY).matches && this.swiper?.allowClick !== false) {
      this.openZoom(Number(photo.dataset.zoomIndex));
      return;
    }

    if (event.target.closest('[data-reviews-link]')) this.scrollToReviews(event);
  };

  /**
   * The gallery's photos, once each (a looping carousel copies its slides), in order.
   * @returns {HTMLImageElement[]}
   */
  get zoomImages() {
    return [...this.querySelectorAll('.featured-product-v2__slider .swiper-slide:not(.swiper-slide-duplicate) [data-zoom-index]')].sort(
      (a, b) => Number(a.dataset.zoomIndex) - Number(b.dataset.zoomIndex)
    );
  }

  /**
   * @param {number} index - The photo's place among the gallery's photos (videos are not counted)
   */
  openZoom(index) {
    this.querySelector('product-zoom')?.open(this.zoomImages, index, (position) => {
      const current = this.swiper?.slides[this.swiper.activeIndex];
      return current?.querySelector(`[data-zoom-index="${position}"]`) ?? null;
    });
  }

  /**
   * Turns the gallery, without animating, to the photo the zoom is closing on, so the zoom closes into it.
   * @param {CustomEvent<{ index: number }>} event
   */
  onZoomClose = (event) => {
    const slide = this.zoomImages[event.detail.index]?.closest('.swiper-slide');
    if (!slide || !this.swiper) return;

    if (this.swiper.params.loop) this.swiper.slideToLoop(Number(slide.dataset.swiperSlideIndex), 0);
    else this.swiper.slideTo([...this.swiper.slides].indexOf(slide), 0);
  };

  /**
   * Phones, classic layout: shows a tab's content, or the benefit bullets, in the sheet. The content is moved rather
   * than copied, so a live widget in it (the Okendo carousel) keeps working; it goes back when the sheet closes.
   * @param {HTMLElement} trigger
   */
  openSheet(trigger) {
    const sheet = this.sheet;
    const source = document.getElementById(trigger.dataset.sheetSource);
    if (!sheet || !source || sheet.open) return;

    // Escape closes the dialog natively and its `close` event puts the content back; this covers that event arriving late
    this.onSheetClose();
    const node = source.querySelector('.featured-product-v2__tab-content') ?? source;
    this.sheetContent = { node, parent: node.parentElement, next: node.nextSibling };
    sheet.querySelector('.featured-product-v2__sheet-body').append(node);
    // Lowercased for the title's capitalize, as live writes "How To Drink" and "100% Happiness Guarantee"
    const title = trigger.getAttribute('aria-label') || trigger.textContent;
    sheet.querySelector('.featured-product-v2__sheet-title').textContent = title.trim().toLowerCase();
    sheet.classList.toggle('featured-product-v2__sheet--card', trigger.dataset.sheetStyle === 'card');

    for (const chip of this.querySelectorAll('.featured-product-v2__chip')) {
      chip.classList.toggle('is-active', chip.dataset.sheetSource === trigger.dataset.sheetSource);
    }

    if (trigger.dataset.sheetStyle !== 'card') {
      sheet.showModal();
      return;
    }

    // The card (the guarantee's (i)) is live's tooltip: part of the page, centred on the product form, so it scrolls
    // under the sticky header and announcement bar. A modal dialog would sit in the top layer, over them.
    sheet.show();
    const form = (trigger.closest('form') ?? trigger).getBoundingClientRect();
    const frame = sheet.offsetParent ?? document.documentElement;
    const { top, left } = frame.getBoundingClientRect();
    sheet.style.setProperty('--fpv2-sheet-top', `${form.top + form.height / 2 - top - frame.clientTop}px`);
    sheet.style.setProperty('--fpv2-sheet-left', `${form.left + form.width / 2 - left - frame.clientLeft}px`);

    // Without the modal's own handling, a press outside the card or Escape closes it, as live's tooltip closes
    this.cardAbort = new AbortController();
    const closeCard = (event) => {
      if (event.type === 'keydown' ? event.key !== 'Escape' : sheet.contains(event.target)) return;
      sheet.close();
    };
    document.addEventListener('pointerdown', closeCard, { signal: this.cardAbort.signal });
    document.addEventListener('keydown', closeCard, { signal: this.cardAbort.signal });
  }

  // Ignored while the sheet is open: a late `close` from the previous use must not empty the one now showing
  onSheetClose = () => {
    if (!this.sheetContent || this.sheet?.open) return;

    this.cardAbort?.abort();
    this.cardAbort = null;
    const { node, parent, next } = this.sheetContent;
    parent.insertBefore(node, next);
    this.sheetContent = null;
  };

  /**
   * @param {MouseEvent} event
   */
  scrollToReviews(event) {
    const widget = document.querySelector('[data-oke-widget]');
    if (!widget) return;

    event.preventDefault();
    widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // The link's own jump is cancelled, so focus is moved too, or the next Tab carries on from the link.
    widget.setAttribute('tabindex', '-1');
    widget.focus({ preventScroll: true });
  }

  /**
   * Rewrites the single product line as an `items[]` list when the add holds
   * more than one line: the bundle's items in place of the bundle product,
   * then the free gifts: the product's any-purchase gifts, and with a
   * subscription its subscription gifts. Gifts go in at their
   * regular price and the theme never changes it; the discount function
   * prices them. A gift's
   * `_gift_parent` names the variants it came with (the product's, or the
   * bundle's items), so the cart engine can remove it once they leave.
   * @param {FormDataEvent} event
   */
  onFormData = (event) => {
    const data = event.formData;
    const bundlePlans = this.applyPurchaseOption(data);
    const sellingPlan = data.get('selling_plan');
    const giftVariantIds = this.giftVariantIds(Boolean(sellingPlan));
    if (!this.bundleItems.length && !giftVariantIds.length) return;

    const variantId = data.get('id');
    const properties = this.takeProperties(data);
    const bundleItems = this.bundleItems.map((item) => ({ ...item, sellingPlan: bundlePlans[item.id] ?? item.sellingPlan }));
    const lines = this.bundleItems.length
      ? window.Cart.bundleLines(bundleItems, variantId, Boolean(sellingPlan), parseInt(data.get('quantity'), 10) || 1).map(
          (line) => ({ ...line, properties: { ...line.properties, ...properties } })
        )
      : [{ id: variantId, quantity: data.get('quantity'), sellingPlan, properties }];
    const giftParent = lines.map((line) => line.id).join(',');

    for (const giftVariantId of giftVariantIds) {
      lines.push({
        id: giftVariantId,
        quantity: 1,
        sellingPlan: null,
        properties: { _free_gift: 'true', _gift_parent: giftParent },
      });
    }

    for (const key of ['id', 'quantity', 'selling_plan']) data.delete(key);

    for (const [index, line] of lines.entries()) {
      const prefix = `items[${index}]`;
      data.append(`${prefix}[id]`, line.id);
      data.append(`${prefix}[quantity]`, line.quantity);
      if (line.sellingPlan) data.append(`${prefix}[selling_plan]`, line.sellingPlan);
      for (const [name, value] of Object.entries(line.properties)) {
        data.append(`${prefix}[properties][${name}]`, value);
      }
    }
  };

  /**
   * Applies the chosen purchase option to the add. A one-time "Buy once" drops the plan and adds one unit. Otherwise
   * the checked `selling_plan` input can carry its own add (snippets/component-supply-plans.liquid): `data-quantity`
   * (a 3-month supply adds 3), `data-properties` (line properties, as JSON) and `data-bundle-plans` (for a bundle,
   * each item's plan by its variant id, as JSON).
   * @param {FormData} data
   * @returns {Record<string, number>} The bundle items' plans by variant id, empty when the option has none
   */
  applyPurchaseOption(data) {
    if (this.purchaseOnce) {
      data.delete('selling_plan');
      data.set('quantity', '1');
      return {};
    }

    const option = this.form?.querySelector('[name="selling_plan"]:checked');
    if (!option) return {};

    if (option.dataset.quantity) data.set('quantity', option.dataset.quantity);
    for (const [name, value] of Object.entries(this.parseJson(option.dataset.properties))) {
      data.set(`properties[${name}]`, value);
    }

    return this.parseJson(option.dataset.bundlePlans);
  }

  /**
   * @param {string | undefined} json
   * @returns {Record<string, any>}
   */
  parseJson(json) {
    if (!json) return {};

    try {
      return JSON.parse(json);
    } catch (error) {
      console.error('Featured product: invalid purchase option data', error);
      return {};
    }
  }

  /**
   * The gifts this add carries, each once: the product's
   * `custom.gift_products_any` always, its `custom.gift_products` only when a
   * selling plan was chosen. A gift the cart already holds is left out: the
   * gift discount frees one unit, so a second add would put in a charged one.
   * @param {boolean} isSubscription
   * @returns {string[]}
   */
  giftVariantIds(isSubscription) {
    const ids = [...(this.dataset.anyGiftIds?.split(',') ?? [])];
    if (isSubscription) ids.push(...(this.dataset.subscriptionGiftIds?.split(',') ?? []));

    const inCart = new Set((window.Cart?.state?.items || []).map((item) => String(item.id)));
    return [...new Set(ids.filter(Boolean))].filter((id) => !inCart.has(id));
  }

  /**
   * @param {Event} event
   */
  onChange = (event) => {
    if (event.target.matches('.selling-plan-cards__input, .selling-plan-cards__toggle-input')) this.syncFreekitImage();
  };

  /**
   * The featured photo shows the product's free-kit image with a subscription and its own photo with a one-time
   * purchase. Every copy is swapped (Swiper's loop duplicates, the thumbnail) along with the zoom's source.
   */
  syncFreekitImage() {
    const toggle = this.querySelector('.selling-plan-cards__toggle-input');
    const plan = this.querySelector('.selling-plan-cards__input:checked');
    if (!toggle && !plan) return;

    const subscribed = toggle ? toggle.checked : plan.value !== '';
    for (const image of this.querySelectorAll('img[data-onetime-src]:not([data-onetime-src=""])')) {
      image.dataset.subscribeSrc ??= image.getAttribute('src');
      image.dataset.subscribeSrcset ??= image.getAttribute('srcset') ?? '';
      if (image.dataset.zoomSrc) {
        image.dataset.subscribeZoom ??= image.dataset.zoomSrc;
        image.dataset.subscribeZoomWidth ??= image.dataset.zoomWidth;
        image.dataset.subscribeZoomHeight ??= image.dataset.zoomHeight;
      }

      const mode = subscribed ? 'subscribe' : 'onetime';
      image.srcset = image.dataset[`${mode}Srcset`] || '';
      image.src = image.dataset[`${mode}Src`];
      if (image.dataset.zoomSrc) {
        image.dataset.zoomSrc = image.dataset[`${mode}Zoom`];
        image.dataset.zoomWidth = image.dataset[`${mode}ZoomWidth`];
        image.dataset.zoomHeight = image.dataset[`${mode}ZoomHeight`];
      }
    }
  }

  /**
   * Moves top-level `properties[...]` fields (from app blocks, or a purchase
   * option's `_supply_length`) off the form data so they can travel with the
   * product's own line, or with each of a bundle's item lines.
   * @param {FormData} data
   * @returns {Record<string, FormDataEntryValue>}
   */
  takeProperties(data) {
    const properties = {};

    for (const [key, value] of [...data.entries()]) {
      const name = key.match(/^properties\[(.+)\]$/)?.[1];
      if (!name) continue;

      properties[name] = value;
      data.delete(key);
    }

    return properties;
  }
}

if (!customElements.get('featured-product-v2')) {
  customElements.define('featured-product-v2', FeaturedProductV2);
}

/**
 * Size picker (the `size_selector` block): one button per sibling product, e.g. Nandaka's 28 and 14 sachet cartons.
 * Choosing one points the form at that product's variant and swaps its plan id, prices and per-day prices into the
 * subscribe / one-time cards, as live's toggle does; the gallery, title and tabs stay on the section's product.
 * @extends HTMLElement
 */
/**
 * Points the form at a variant and swaps its plan id and prices into the selling-plan cards, from the `data-*`
 * attributes component-selling-plan-cards writes in data_only mode. Without cards the price goes straight to the
 * buy buttons.
 * @param {HTMLFormElement} form
 * @param {DOMStringMap} data
 */
function applyVariantData(form, data) {
  const variantInput = form.querySelector('input[name="id"]');
  if (variantInput) variantInput.value = data.variantId || '';

  const subscribe = form.querySelector('.selling-plan-cards__input:not([value=""])');
  const onetime = form.querySelector('.selling-plan-cards__input[value=""]');

  if (subscribe && data.sellingPlan) {
    subscribe.disabled = false;
    subscribe.value = data.sellingPlan;
    subscribe.dataset.price = data.subscribePrice || '';
    const crossedOut = data.onSale === 'true' ? data.onetimePrice : '';
    updateCard(subscribe, data.subscribeDisplay || data.subscribePrice, data.subscribeDay, crossedOut);
  } else if (subscribe) {
    // A variant with no plan can only be bought once: a disabled radio sends nothing
    subscribe.disabled = true;
    if (onetime) onetime.checked = true;
  }

  if (onetime) {
    onetime.dataset.price = data.onetimePrice || '';
    updateCard(onetime, data.onetimePrice, data.onetimeDay, '');
  }

  const cards = form.querySelector('selling-plan-cards');
  if (cards?.updatePrice) {
    cards.updatePrice();
  } else if (data.onetimePrice) {
    // Buttons outside the form (the sticky bars) join it with the `form` attribute
    const formId = form.getAttribute('id');
    const outside = formId ? document.querySelectorAll(`[form="${formId}"] [data-purchase-price]`) : [];
    for (const element of [...form.querySelectorAll('[data-purchase-price]'), ...outside]) {
      element.textContent = data.onetimePrice;
    }
  }

  const button = form.querySelector('.featured-product-v2__atc');
  if (button) button.disabled = data.available === 'false';
}

/**
 * Rewrites one card's price, crossed-out price and per-day line.
 * @param {HTMLInputElement} input
 * @param {string | undefined} price
 * @param {string | undefined} day
 * @param {string} compare - The crossed-out price, or empty to hide it
 */
function updateCard(input, price, day, compare) {
  const card = input.closest('.selling-plan-cards__option');
  if (!card || !price) return;

  const main = card.querySelector('.selling-plan-cards__price:not(.selling-plan-cards__price--compare)');
  if (main) main.textContent = price;

  const crossed = card.querySelector('.selling-plan-cards__price--compare');
  if (crossed) {
    crossed.textContent = compare;
    crossed.hidden = !compare;
  }

  const perDay = card.querySelector('.selling-plan-cards__day');
  if (perDay && day) perDay.textContent = day;
}

export class FeaturedProductSizeSelector extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  get form() {
    return this.closest('form');
  }

  /**
   * @param {MouseEvent} event
   */
  onClick = (event) => {
    const button = event.target.closest('.featured-product-v2__size');
    if (!button || button.disabled || button.classList.contains('is-active')) return;

    this.select(button);
  };

  /**
   * @param {HTMLButtonElement} button
   */
  select(button) {
    const form = this.form;
    if (!form) return;

    for (const other of this.querySelectorAll('.featured-product-v2__size')) {
      const active = other === button;
      other.classList.toggle('is-active', active);
      other.setAttribute('aria-pressed', String(active));
    }

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = button.dataset.variantId || '';

    const data = button.dataset;
    const subscribe = form.querySelector('.selling-plan-cards__input:not([value=""])');
    const onetime = form.querySelector('.selling-plan-cards__input[value=""]');

    if (subscribe && data.sellingPlan) {
      subscribe.disabled = false;
      subscribe.value = data.sellingPlan;
      subscribe.dataset.price = data.subscribePrice || '';
      const crossedOut = data.onSale === 'true' ? data.onetimePrice : '';
      this.updateCard(subscribe, data.subscribeDisplay || data.subscribePrice, data.subscribeDay, crossedOut);
    } else if (subscribe) {
      // A size with no plan can only be bought once: the subscribe card is switched off (a disabled radio sends
      // nothing), so the previous size's plan never goes in with this variant.
      subscribe.disabled = true;
      if (onetime) onetime.checked = true;
    }

    if (onetime) {
      onetime.dataset.price = data.onetimePrice || '';
      this.updateCard(onetime, data.onetimePrice, data.onetimeDay, '');
    }

    this.updateToggle(form, data);
    this.updateBar(form, data);
    form.querySelector('selling-plan-cards')?.updatePrice?.();
    this.updateButton(form, data.available !== 'false');
  }

  /**
   * The sticky bars' prices (outside the form): the plan price, the crossed-out regular price and the one-time
   * price, as live's sachet toggle rewrites its bar. Their buttons follow through `updatePrice`.
   * @param {HTMLFormElement} form
   * @param {DOMStringMap} data - The size button's plan and prices
   */
  updateBar(form, data) {
    const root = form.closest('featured-product-v2');
    if (!root) return;

    for (const bar of root.querySelectorAll('[data-sticky-bar]')) {
      const set = (selector, text) => {
        const element = bar.querySelector(selector);
        if (element && text) element.textContent = text;
      };
      set(
        '.featured-product-v2__bar-price--on:not(.featured-product-v2__bar-price--compare)',
        data.subscribeDisplay || data.subscribePrice
      );
      set('.featured-product-v2__bar-price--off', data.onetimePrice);
      const compare = bar.querySelector('.featured-product-v2__bar-price--compare');
      if (compare && data.onetimePrice) {
        const label = compare.querySelector('.visually-hidden');
        compare.textContent = '';
        if (label) compare.append(label, ' ');
        compare.append(data.onetimePrice);
      }
    }
  }

  /**
   * The toggle layout (the product page's one card with a switch): the switch carries the plan id and both prices,
   * and the card prints the subscribe price over the regular one, the one-time price, and a per-day line for each.
   * @param {HTMLFormElement} form
   * @param {DOMStringMap} data - The size button's plan and prices
   */
  updateToggle(form, data) {
    const toggle = form.querySelector('.selling-plan-cards__toggle-input');
    if (!toggle) return;

    if (data.sellingPlan) {
      toggle.disabled = false;
      toggle.value = data.sellingPlan;
      toggle.dataset.price = data.subscribePrice || '';
    } else {
      toggle.checked = false;
      toggle.disabled = true;
    }
    toggle.dataset.priceOff = data.onetimePrice || '';

    const card = toggle.closest('.selling-plan-cards');
    if (!card) return;
    const set = (selector, text) => {
      const element = card.querySelector(selector);
      if (element && text) element.textContent = text;
    };
    set('.selling-plan-cards__card-price--sale', data.subscribeDisplay || data.subscribePrice);
    set('.selling-plan-cards__card-price--onetime', data.onetimePrice);
    set('.selling-plan-cards__card-serving.selling-plan-cards__when-on', data.subscribeDay);
    set('.selling-plan-cards__card-serving.selling-plan-cards__when-off', data.onetimeDay);
    const compare = card.querySelector('.selling-plan-cards__card-price--compare');
    if (compare) {
      compare.hidden = data.onSale !== 'true';
      const label = compare.querySelector('.visually-hidden');
      compare.textContent = '';
      if (label) compare.append(label, ' ');
      compare.append(data.onetimePrice || '');
    }
  }

  /**
   * Rewrites one card's price, crossed-out price and per-day line.
   * @param {HTMLInputElement} input
   * @param {string | undefined} price
   * @param {string | undefined} day
   * @param {string} compare - The crossed-out price, or empty to hide it
   */
  updateCard(input, price, day, compare) {
    const card = input.closest('.selling-plan-cards__option');
    if (!card || !price) return;

    const main = card.querySelector('.selling-plan-cards__price:not(.selling-plan-cards__price--compare)');
    if (main) main.textContent = price;

    const crossed = card.querySelector('.selling-plan-cards__price--compare');
    if (crossed) {
      crossed.textContent = compare;
      crossed.hidden = !compare;
    }

    const perDay = card.querySelector('.selling-plan-cards__day');
    if (perDay && day) perDay.textContent = day;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} available
   */
  updateButton(form, available) {
    const button = form.querySelector('.featured-product-v2__atc');
    if (!button) return;

    button.disabled = !available;
  }
}

if (!customElements.get('featured-product-size-selector')) {
  customElements.define('featured-product-size-selector', FeaturedProductSizeSelector);
}

/** Live's option list sits left of its button from here; below it, a bottom sheet */
const VARIANT_POPOVER_SIDE_QUERY = '(min-width: 1008px)';

/**
 * Variant picker: per option a button opening a popover list of values (live's Prestige OptionSelector). The
 * chosen combination's variant is applied to the form and the URL.
 * @extends HTMLElement
 */
export class FeaturedProductVariantPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    for (const popover of this.querySelectorAll('[popover]')) popover.addEventListener('toggle', this.onToggle);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    for (const popover of this.querySelectorAll('[popover]')) popover.removeEventListener('toggle', this.onToggle);
  }

  /**
   * @param {MouseEvent} event
   */
  onClick = (event) => {
    const value = event.target.closest('.featured-product-v2__variant-value');
    if (!value) return;

    const option = value.closest('.featured-product-v2__variant-option');
    for (const other of option.querySelectorAll('.featured-product-v2__variant-value')) {
      const selected = other === value;
      other.classList.toggle('is-selected', selected);
      other.setAttribute('aria-selected', String(selected));
    }
    option.querySelector('[data-variant-value]').textContent = value.dataset.value;
    option.querySelector('[popover]').hidePopover();
    this.applySelection();
  };

  /**
   * @param {ToggleEvent} event
   */
  onToggle = (event) => {
    const popover = event.target;
    const trigger = popover.closest('.featured-product-v2__variant-option').querySelector('.featured-product-v2__variant-trigger');
    const open = event.newState === 'open';
    trigger.setAttribute('aria-expanded', String(open));

    if (!open) {
      trigger.focus();
      return;
    }

    // From 1008px the list sits 13px left of the button, centred on it
    if (window.matchMedia(VARIANT_POPOVER_SIDE_QUERY).matches) {
      const box = trigger.getBoundingClientRect();
      popover.style.setProperty('--featured-product-v2-popover-top', `${box.top + box.height / 2}px`);
      popover.style.setProperty('--featured-product-v2-popover-right', `${document.documentElement.clientWidth - box.left + 13}px`);
    }
    (popover.querySelector('.is-selected') ?? popover.querySelector('.featured-product-v2__variant-value'))?.focus();
  };

  applySelection() {
    // The picker can sit outside the form in some layouts
    const form = this.closest('form') ?? this.closest('.featured-product-v2')?.querySelector('form[action*="/cart/add"]');
    const selected = [...this.querySelectorAll('[data-variant-value]')].map((element) => element.textContent);
    const entries = this.querySelector('template[data-variant-data]')?.content.querySelectorAll('[data-variant-id]') ?? [];
    const match = [...entries].find((entry) => JSON.stringify(JSON.parse(entry.dataset.options)) === JSON.stringify(selected));
    if (!form || !match) return;

    applyVariantData(form, match.dataset);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', match.dataset.variantId);
    window.history.replaceState({}, '', url);
  }
}

if (!customElements.get('featured-product-variant-picker')) {
  customElements.define('featured-product-variant-picker', FeaturedProductVariantPicker);
}
if (!customElements.get('featured-product-size-selector')) {
  customElements.define('featured-product-size-selector', FeaturedProductSizeSelector);
}

/**
 * The quantity stepper over the add-to-cart button on live's Default template (the books, the Codex): two buttons
 * step the number input the form posts as `quantity`, never below its `min`.
 */
class FeaturedProductQuantity extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('input[name="quantity"]');
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const button = event.target.closest('[data-quantity-step]');
    if (!button || !this.input) return;

    const min = Number(this.input.min) || 1;
    const next = (parseInt(this.input.value, 10) || min) + Number(button.dataset.quantityStep);
    this.input.value = Math.max(min, next);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('featured-product-quantity')) {
  customElements.define('featured-product-quantity', FeaturedProductQuantity);
}
